import React, { useState, useEffect } from "react";
import { Home, ArrowLeft, FileSearch } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Meta from "../../components/Meta";

export default function ChecklistNotFound() {
  const [isVisible, setIsVisible] = useState(false);
  const [floatingElements, setFloatingElements] = useState([]);

  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate("/");
  };

  useEffect(() => {
    setIsVisible(true);

    // Create floating elements
    const elements = Array.from({ length: 6 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 3 + Math.random() * 2,
    }));
    setFloatingElements(elements);
  }, []);

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <>
      <Meta
        title="Checklist Not Found - Govind Sinha & Co."
        ogTitle="Custom Checklist - Govind Sinha & Co."
        ogDescription="This url leads to a custom checklist. The content may not be associated with Govind Sinha & Co. as anyone can generate a checklist."
        description="The checklist you are looking for could not be found."
        canonical="https://cagovindsinha.com/checklists/not-found"
      />
      <div className="relative flex items-center justify-center min-h-screen p-4 pt-16 overflow-hidden bg-gradient-to-br from-blue-50 via-purple-50 to-yellow-50">
        <Navbar />
        {/* Floating background elements */}
        {floatingElements.map((element) => (
          <div
            key={element.id}
            className="absolute w-20 h-20 rounded-full opacity-20"
            style={{
              left: `${element.x}%`,
              top: `${element.y}%`,
              background: `linear-gradient(45deg, ${
                element.id % 3 === 0
                  ? "#fbbf24"
                  : element.id % 3 === 1
                  ? "#3b82f6"
                  : "#8b5cf6"
              }, ${
                element.id % 3 === 0
                  ? "#f59e0b"
                  : element.id % 3 === 1
                  ? "#1d4ed8"
                  : "#7c3aed"
              })`,
              animation: `float ${element.duration}s ease-in-out infinite`,
              animationDelay: `${element.delay}s`,
            }}
          />
        ))}

        {/* Main content */}
        <div
          className={`max-w-4xl mx-auto text-center transform transition-all duration-1000 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          {/* Icon */}
          <div className="relative mb-8">
            <FileSearch className="w-32 h-32 mx-auto text-gray-500" />
          </div>

          {/* Title */}
          <div className="mb-6 space-y-2">
            <h2 className="mb-2 text-3xl font-bold text-gray-800 md:text-4xl">
              Checklist Not Found
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-gray-600 md:text-xl">
              We couldn't find the checklist you're looking for. It might have
              been moved, deleted, or never existed.
            </p>
          </div>

          {/* Decorative elements */}
          <div className="flex items-center justify-center mb-8 space-x-4">
            <div className="w-3 h-3 bg-yellow-400 rounded-full animate-bounce"></div>
            <div
              className="w-3 h-3 bg-blue-400 rounded-full animate-bounce"
              style={{ animationDelay: "0.1s" }}
            ></div>
            <div
              className="w-3 h-3 bg-purple-400 rounded-full animate-bounce"
              style={{ animationDelay: "0.2s" }}
            ></div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col items-center justify-center gap-4 mb-8 sm:flex-row">
            <button
              onClick={handleGoHome}
              className="flex items-center gap-2 px-6 py-3 font-medium text-white transition-all duration-300 transform rounded-full shadow-lg group bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 hover:scale-105 hover:shadow-xl"
            >
              <Home className="w-5 h-5 transition-transform group-hover:rotate-12" />
              Go Home
            </button>

            <button
              onClick={handleGoBack}
              className="flex items-center gap-2 px-6 py-3 font-medium text-white transition-all duration-300 transform rounded-full shadow-lg group bg-gradient-to-r from-purple-500 to-yellow-500 hover:from-purple-600 hover:to-yellow-600 hover:scale-105 hover:shadow-xl"
            >
              <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
              Go Back
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
